/**
 * Da dove si comincia: chi sei, qual e' il tuo lavoro, che cosa fai adesso.
 *
 * **Non e' un tour e non e' un cruscotto.** Un tour a passi si attraversa una
 * volta senza leggerlo ed e' la prima cosa che invecchia; un cruscotto di
 * cifre senza azione si guarda un giorno e poi mai piu'. Questo e' cio' che
 * sta in cima alla schermata su cui si arriva entrando, e dice tre cose: come
 * ti chiama il prodotto, che cosa ci si aspetta da te, e — dove non e' ovvio —
 * che cosa **non** e' tuo.
 *
 * **Che cosa non e' tuo conta quanto il resto.** I permessi funzionano da
 * sempre, ma in silenzio: chi non vede la consegna crede che manchi o che sia
 * rotta, non che sia di un altro. Dirlo trasforma un'assenza inspiegabile in
 * una divisione del lavoro.
 *
 * **Non si nasconde e non si chiude.** Un riquadro che si puo' chiudere una
 * volta per sempre e' un riquadro che chi entra il primo giorno chiude per
 * sbaglio e non ritrova. Sono due righe in cima a una schermata: l'occhio di
 * chi le ha gia' lette le salta.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { readSettings } from '../admin/settings'
import { namedRole } from '../admin/users'
import { SETTINGS_MANAGE, type Whoami } from '../auth'
import { useT } from '../i18n/LocaleProvider'
import { Figures } from './Figures'
import type { MessageKey } from '../i18n/messages'

/** Che cosa dice il riquadro, per ogni mestiere. */
const SAYS: Record<string, MessageKey> = {
  uploader: 'start.uploader',
  qa_operator: 'start.qa',
  supervisor: 'start.supervisor',
  job_manager: 'start.manager',
}

export function StartPanel({ who, children }: { who: Whoami; children?: ReactNode }): JSX.Element | null {
  const t = useT()
  const says = SAYS[who.role]
  // Un ruolo che non conosciamo non riceve una frase inventata: senza testo il
  // riquadro non compare, e la schermata resta quella di prima.
  if (says === undefined) return null
  // Il riquadro spiegava il mestiere in quattro righe sopra la tabella, a
  // ogni ingresso: la prima volta serve, poi ruba lo schermo al lavoro (S17,
  // L-4). Aperto la prima volta, poi un titolo con il ⓘ che si riapre.
  // Per utenza, non per navigatore (GI-281): due persone sullo stesso posto
  // di lavoro non si chiudono il riquadro a vicenda.
  const [open, setOpen] = useState<boolean>(() => firstTime(who.login))
  return (
    <details
      className="start-panel"
      aria-label={t('start.here')}
      open={open}
      onToggle={(event) => {
        const next = (event.currentTarget as HTMLDetailsElement).open
        setOpen(next)
        if (!next) markSeen(who.login)
      }}
    >
      <summary className="start-summary">
        <span className="start-role">{namedRole(t, who.role)}</span>
        <span className="start-info" aria-hidden="true">
          ⓘ
        </span>
      </summary>
      <p className="start-says">{t(says)}</p>
      {/* La barra e' filtrata dai permessi da sempre; senza questa riga
          l'operatore che ne vede una sola crede che le altre manchino. */}
      <p className="start-rail tenue">{t('start.rail')}</p>
      {/* Dove sta il resto: il manuale del mestiere, le parole, i tasti. Il
          riquadro dice il primo passo; il manuale dice il resto (GI-281). */}
      <p className="start-aiuto tenue">
        {t('start.help')} <Link to="/aiuto">{t('rail.help')}</Link>
      </p>
      {who.may.includes(SETTINGS_MANAGE) && <Aperte />}
      {/* Le cifre stanno sotto le decisioni aperte: prima cio' che costa ogni
          giorno, poi il conto della settimana. */}
      {who.may.includes(SETTINGS_MANAGE) && <Figures who={who} />}
      {children}
    </details>
  )
}

const SEEN = 'gridfly.start.seen'

function firstTime(login: string): boolean {
  try {
    return window.localStorage.getItem(`${SEEN}.${login}`) === null
  } catch {
    return true
  }
}

function markSeen(login: string): void {
  try {
    window.localStorage.setItem(`${SEEN}.${login}`, '1')
  } catch {
    // Senza memoria si riapre ogni volta: non e' un guasto.
  }
}


/**
 * Che cosa aspetta una decisione, per chi amministra.
 *
 * **Una cifra sola, e quella giusta.** Le ipotesi sono le scelte che abbiamo
 * preso al posto del committente mentre lui non rispondeva: sono la sola cosa
 * di questa schermata che diventa piu' cara ogni giorno che passa, perche' una
 * consegna prodotta sotto un'ipotesi sbagliata si scopre alla contestazione.
 *
 * **Se non si sa, non si dice.** Un guasto della lettura lascia il riquadro
 * senza riga invece di scrivere zero: «nessuna decisione aperta» e' una buona
 * notizia, e darla per un errore di rete e' il modo piu' rapido di far smettere
 * di guardare qui.
 */
function Aperte(): JSX.Element | null {
  const t = useT()
  const [quante, setQuante] = useState<number | null>(null)

  useEffect(() => {
    let vivo = true
    void readSettings()
      .then((found) => {
        if (vivo) setQuante(found.filter((one) => one.source === 'hypothesis').length)
      })
      .catch(() => {
        // Silenzio voluto: si veda il commento sopra.
      })
    return () => {
      vivo = false
    }
  }, [])

  if (quante === null) return null
  return (
    <p className="start-open">
      {quante === 0 ? t('start.hypothesesNone') : t('start.hypotheses', { n: quante })}
      {quante > 0 && (
        <>
          {' '}
          <Link to="/admin/settings">{t('start.toSettings')}</Link>
        </>
      )}
    </p>
  )
}
